import cart from '../../cart/scripts/cart.js';

// Never shrink the order-line list below this, and leave this much breathing
// room below the card when the sticky order column is pinned to the top.
const MIN_LIST_HEIGHT = 160;
const CARD_BOTTOM_GAP = 24;

class CheckoutOrderOverview {
  constructor(container) {
    this.container = container;
    this.cards = container.querySelectorAll('.checkout-order__card');
    this.busy = false;
  }

  init() {
    this.container.addEventListener('click', event => {
      const plus = event.target.closest('[data-col-plus]');
      const minus = event.target.closest('[data-col-minus]');
      const remove = event.target.closest('[data-col-remove]');
      const item = event.target.closest('[data-line-key]');
      if (!item) return;

      if (plus || minus) this.step(item, plus ? 1 : -1);
      else if (remove) this.mutate(item.dataset.lineKey, 0);
    });
  }

  step(item, delta) {
    const quantity = parseInt(item.dataset.itemQuantity, 10) + delta;
    if (quantity < 1) return;
    this.mutate(item.dataset.lineKey, quantity);
  }

  async mutate(key, quantity) {
    if (this.busy) return;
    this.busy = true;
    this.setLoading(true);
    try {
      const response = await cart.changeItem(key, quantity);
      const data = await response.json().catch(() => null);
      await this.refresh(data);
    } catch (error) {
      console.warn('[checkout-order-overview]', error);
    } finally {
      this.setLoading(false);
      this.busy = false;
    }
  }

  setLoading(on) {
    this.cards.forEach(card => card.querySelector('.cart-line__items')?.classList.toggle('is-loading', on));
  }

  async refresh(cartData) {
    const response = await window.fetch(window.location.pathname, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) return;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const freshItems = doc.querySelector('.checkout-order__card .cart-line__items');
    const freshHint = doc.querySelector('.checkout-order__card .co-line__scroll-hint');
    if (!freshItems) return;

    this.cards.forEach(card => {
      const items = card.querySelector('.cart-line__items');
      if (items) items.replaceWith(freshItems.cloneNode(true));

      const hint = card.querySelector('.co-line__scroll-hint');
      if (hint) hint.remove();
      if (freshHint) {
        card.querySelector('.cart-line__items')?.insertAdjacentElement('afterend', freshHint.cloneNode(true));
      }

      if (cartData && typeof cartData.total_price === 'number') {
        const subtotal = card.querySelector('[data-checkout-subtotal]');
        if (subtotal) subtotal.dataset.subtotalPrice = cartData.total_price;
      }
    });

    document.dispatchEvent(new CustomEvent('checkout:updated', {
      detail: {
        discountAmount: window.checkout?.discountAmountCombined ?? null,
        discountAmountInLiveCartTotal: window.checkout?.discountAmountInLiveCartTotal ?? 0,
      },
    }));
  }
}

// Scroll-hint: clicking the hint scrolls the order line list to the next hidden
// item; the hint hides at the bottom and reappears when items below are hidden.
// Bound to the persistent container with delegation/capture so it survives the
// CheckoutOrderOverview re-render that replaces the list.
class CartLineScroll {
  constructor(container) {
    this.container = container;
  }

  init() {
    this.container.addEventListener('click', event => {
      const hint = event.target.closest('.co-line__scroll-hint');
      if (hint) this.scrollNext(hint);
    });
    this.container.addEventListener('scroll', event => {
      const list = event.target.closest?.('.cart-line__items--scroll');
      if (list) this.updateHint(list);
    }, true);
    document.addEventListener('checkout:updated', () => this.syncAll());
    window.addEventListener('resize', () => this.syncAll());
    window.addEventListener('load', () => this.syncAll());
    this.syncAll();
  }

  // Height of 3 full items + a quarter of the 4th, so the 4th peeks. null if ≤3.
  peekHeight(items) {
    if (items.length <= 3) return null;
    return items[0].offsetHeight + items[1].offsetHeight + items[2].offsetHeight + items[3].offsetHeight * 0.25;
  }

  // Distance from the viewport top to the card once the order column is pinned.
  stuckTop(card) {
    const column = card.closest('.checkout__order-overview');
    if (!column) return 0;
    const cs = getComputedStyle(column);
    return (parseFloat(cs.top) || 0) + (parseFloat(cs.paddingTop) || 0);
  }

  // Size the line list so (a) it peeks the 4th item when there are more than 3,
  // and (b) on desktop the whole card stays inside the viewport once the sticky
  // column is pinned — shrinking the list to scroll even with fewer than 4 items.
  fit(card) {
    const list = card.querySelector('.cart-line__items');
    if (!list || !card.offsetParent) return;
    const items = [...list.querySelectorAll('.cart-line__item')];
    if (!items.length) return;

    const naturalList = list.scrollHeight;
    const restOfCard = card.offsetHeight - list.offsetHeight;
    let target = naturalList;

    const peek = this.peekHeight(items);
    if (peek) target = Math.min(target, peek);

    if (window.matchMedia('(min-width: 767px)').matches) {
      const available = window.innerHeight - this.stuckTop(card) - CARD_BOTTOM_GAP;
      if (restOfCard + target > available) target = available - restOfCard;
    }

    target = Math.max(MIN_LIST_HEIGHT, Math.min(target, naturalList));

    if (target < naturalList - 1) {
      list.style.maxHeight = `${Math.round(target)}px`;
      list.classList.add('cart-line__items--scroll');
    } else {
      list.style.maxHeight = '';
      if (items.length <= 3) list.classList.remove('cart-line__items--scroll');
    }
    this.updateHint(list);
  }

  listFor(el) {
    return el.closest('.checkout-order__card')?.querySelector('.cart-line__items--scroll');
  }

  hintFor(list) {
    return list.closest('.checkout-order__card')?.querySelector('.co-line__scroll-hint');
  }

  scrollNext(hint) {
    const list = this.listFor(hint);
    if (!list) return;
    const listTop = list.getBoundingClientRect().top;
    const visibleBottom = list.scrollTop + list.clientHeight;
    const items = [...list.querySelectorAll('.cart-line__item')];
    // First item not fully visible (its bottom extends past the fold).
    const next = items.find(item => {
      const bottom = item.getBoundingClientRect().bottom - listTop + list.scrollTop;
      return bottom > visibleBottom + 1;
    });
    if (next) {
      const top = next.getBoundingClientRect().top - listTop + list.scrollTop;
      list.scrollTo({ top, behavior: 'smooth' });
    }
  }

  updateHint(list) {
    const hint = this.hintFor(list);
    if (!hint) return;
    // Reserve the hint's space (visibility, not display) so the card doesn't shrink at the bottom.
    const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
    hint.classList.toggle('co-line__scroll-hint--hidden', atBottom);
  }

  syncAll() {
    this.container.querySelectorAll('.checkout-order__card').forEach(card => this.fit(card));
  }
}

function bootCheckoutOrderOverview() {
  const container = document.querySelector('[data-section-type="checkout-template"]');
  if (!container || !container.querySelector('.checkout-order__card')) return;
  const overview = new CheckoutOrderOverview(container);
  overview.init();
  const scroll = new CartLineScroll(container);
  scroll.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootCheckoutOrderOverview);
} else {
  bootCheckoutOrderOverview();
}

export default CheckoutOrderOverview;
